import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Textarea,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@city-sage/aurum-avis-labs-components';
import { trackEvent } from '@city-sage/aurum-avis-labs-components';
import { toast } from 'sonner';

export type ReportableType = 'comment' | 'user' | 'itinerary' | 'stop';

const reportReasons = [
  { value: 'spam', label: 'Spam or misleading content' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'copyright', label: 'Copyright violation' },
  { value: 'fake', label: 'Fake or misleading information' },
  { value: 'other', label: 'Other' },
] as const;

const reportSchema = z.object({
  reason: z.enum(['spam', 'harassment', 'inappropriate', 'copyright', 'fake', 'other'], {
    required_error: 'Please select a reason for reporting',
  }),
  description: z.string()
    .max(1000, 'Description must be less than 1000 characters')
    .refine((val) => val.trim().length === 0 || val.trim().length >= 10, {
      message: 'If provided, description must be at least 10 characters',
    }),
});

type ReportFormValues = z.infer<typeof reportSchema>;

interface ReportDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  contentId: string;
  contentType: ReportableType;
}

export function ReportDialog({
  isOpen,
  onOpenChange,
  contentId,
  contentType,
}: ReportDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<ReportFormValues>({
    resolver: zodResolver(reportSchema),
    defaultValues: {
      reason: undefined,
      description: '',
    },
  });

  const selectedReason = watch('reason');

  const onSubmit = async (data: ReportFormValues) => {
    setIsSubmitting(true);
    
    try {
      // Track the report event with Application Insights
      trackEvent('ContentReported', {
        contentId,
        contentType,
        reason: data.reason,
        description: data.description,
        timestamp: new Date().toISOString(),
      });

      toast.success('Thank you for your report. We will review it shortly.');
      reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to submit report:', error);
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      onOpenChange(false);
    }
  };

  const contentTypeLabels: Record<ReportableType, string> = {
    comment: 'comment',
    user: 'user',
    itinerary: 'itinerary',
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Report {contentTypeLabels[contentType]}</DialogTitle>
          <DialogDescription>
            Help us keep CitySage safe by reporting content that violates our community guidelines.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reason">Reason for reporting</Label>
            <Select
              value={selectedReason}
              onValueChange={(value) => setValue('reason', value as ReportFormValues['reason'], { shouldValidate: true })}
            >
              <SelectTrigger id="reason" className={errors.reason ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {reportReasons.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.reason && (
              <p className="text-sm text-red-500">{errors.reason.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              Additional details <span className="text-muted-foreground">(optional but helpful)</span>
            </Label>
            <Textarea
              id="description"
              placeholder="Please provide more details about why you're reporting this content..."
              className={errors.description ? 'border-red-500' : ''}
              rows={4}
              {...register('description')}
            />
            {errors.description && (
              <p className="text-sm text-red-500">{errors.description.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

